import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { calculateAssetMonthlyImpact, resolveActiveAssetEvents } from "../../utils/chart/burndown.utils";
import { calculateRevenueAtMonth } from "../../utils/chart/scaling.utils";
import {
  calculateMonthlyCustomerMetrics,
  type CustomerProjectionData,
} from "../../utils/chart/customer-projection.utils";
import { safeDate } from "../../utils/date";
import useResponsive from "../../hooks/useResponsive";

// Using main type definitions from startup.types.ts
import type { Revenue, TimelineEvent } from "../../types/startup.types";

interface RevenueBreakdownProps {
  revenues: Revenue[];
  events?: TimelineEvent[];
  customerProjectionData?: CustomerProjectionData;
  currentDate?: Date; // Timeline position to show breakdown for
}

const CATEGORY_COLORS = {
  funding: "#3b82f6",
  sales: "#10b981",
  investment: "#8b5cf6",
  grant: "#f59e0b",
  "asset appreciation": "#a855f7",
  "subscription revenue": "#059669",
  other: "#6b7280",
};

const RevenueBreakdownChart: React.FC<RevenueBreakdownProps> = ({
  revenues,
  events = [],
  customerProjectionData,
  currentDate = new Date(), // Default to current date if not provided
}) => {
  const { isMobile, isTablet } = useResponsive();

  // Responsive chart sizing
  const chartConfig = useMemo(() => {
    if (isMobile) {
      return {
        cx: "50%",
        cy: "45%",
        innerRadius: 20,
        outerRadius: 50,
        legendHeight: 60,
        showLegend: false,
      };
    } else if (isTablet) {
      return {
        cx: "50%",
        cy: "42%",
        innerRadius: 22,
        outerRadius: 55,
        legendHeight: 50,
        showLegend: true,
      };
    } else {
      return {
        cx: "50%",
        cy: "40%",
        innerRadius: 25,
        outerRadius: 60,
        legendHeight: 40,
        showLegend: true,
      };
    }
  }, [isMobile, isTablet]);

  // Calculate total revenue by category for the specific date
  const chartData = useMemo(() => {
    const categoryTotals: Record<string, number> = {};
    const currentMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );

    // Process regular revenues - only those active at the current date
    revenues.forEach((revenue) => {
      // Skip revenues without start dates
      if (!revenue.startDate) return;

      try {
        // Check if revenue is active at the current date
        const revenueStartDate = safeDate(revenue.startDate);
        const revenueStart = new Date(
          revenueStartDate.getFullYear(),
          revenueStartDate.getMonth(),
          1
        );
        const revenueEnd = revenue.endDate
          ? new Date(
              safeDate(revenue.endDate).getFullYear(),
              safeDate(revenue.endDate).getMonth(),
              1
            )
          : new Date(2099, 11, 31); // Far future if no end date

        // Only include if revenue is active at current date
        if (currentMonth >= revenueStart && currentMonth <= revenueEnd) {
          // Calculate actual amount for this month using scaling utilities
          const revenueForCalculation = {
            ...revenue,
            amount: revenue.monthlyAmount || revenue.amount || 0,
          };

          const monthlyAmount = calculateRevenueAtMonth(
            revenueForCalculation,
            currentMonth
          );

          if (monthlyAmount > 0) {
            const category = revenue.type;
            categoryTotals[category] =
              (categoryTotals[category] || 0) + monthlyAmount;
          }
        }
      } catch (error) {
        console.warn("Error processing revenue:", revenue.id, error);
        // Continue processing other revenues
      }
    });

    const assetEvents = resolveActiveAssetEvents(
      (events ?? []).map((event) => ({
        id: event.id,
        date: safeDate(event.date),
        type: "asset" as const,
        priority: event.priority,
        description: event.description || event.title || "",
        amount: event.amount,
        monthlyAppreciationRate: event.monthlyAppreciationRate,
        minimumValue: event.minimumValue,
        endDate: event.endDate ? safeDate(event.endDate) : undefined,
        lineItemGroupId: event.lineItemGroupId,
        segmentKind: event.segmentKind,
      })),
      currentMonth,
    );

    const totalAssetAppreciation = assetEvents.reduce((total, event) => {
      try {
        const monthlyImpact = calculateAssetMonthlyImpact(event, currentMonth);
        return total + Math.max(0, monthlyImpact);
      } catch (error) {
        console.warn("Error processing asset event:", event.id, error);
        return total;
      }
    }, 0);

    if (totalAssetAppreciation > 0) {
      categoryTotals["asset appreciation"] = totalAssetAppreciation;
    }

    // Calculate customer subscription revenue if available
    if (customerProjectionData) {
      const customerMetrics = calculateMonthlyCustomerMetrics(
        currentMonth,
        customerProjectionData
      );
      if (customerMetrics.subscriptionRevenue > 0) {
        categoryTotals["subscription revenue"] =
          customerMetrics.subscriptionRevenue;
      }
    }

    return Object.entries(categoryTotals)
      .map(([category, amount]) => ({
        name: category.charAt(0).toUpperCase() + category.slice(1),
        value: amount,
        color:
          CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS] ||
          "#6b7280",
      }))
      .sort((a, b) => b.value - a.value);
  }, [revenues, events, customerProjectionData, currentDate]);

  const totalRevenue = chartData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = ((data.value / totalRevenue) * 100).toFixed(1);

      return (
        <div className="bg-surface-elevated text-white text-xs px-3 py-2 rounded-lg shadow-glow">
          <p className="font-semibold">{data.payload.name}</p>
          <p className="text-brand">${data.value.toLocaleString()}/month</p>
          <p className="text-white/55">{percentage}% of total</p>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="bg-surface-elevated rounded-lg border h-full flex flex-col">
        <div className="px-4 py-3 border-b bg-white/[0.06] flex-shrink-0">
          <h4 className="text-sm font-medium text-white/75">
            Revenue Breakdown
          </h4>
          <p className="text-xs text-white/45 mt-1">No revenue data</p>
        </div>
        <div className="p-4 flex-1 flex items-center justify-center">
          <p className="text-white/45 text-sm">No revenue data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-elevated rounded-lg border h-full flex flex-col">
      <div className="px-4 py-3 border-b bg-white/[0.06] flex-shrink-0">
        <h4 className="text-sm font-medium text-white/75">Revenue Breakdown</h4>
        <p className="text-xs text-white/45 mt-1">
          {currentDate.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          })}
          : ${totalRevenue.toLocaleString()}/month
        </p>
      </div>

      <div className="p-4 flex-1 min-h-0 pb-6">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx={chartConfig.cx}
              cy={chartConfig.cy}
              innerRadius={chartConfig.innerRadius}
              outerRadius={chartConfig.outerRadius}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            {chartConfig.showLegend && (
              <Legend
                verticalAlign="bottom"
                height={chartConfig.legendHeight}
                formatter={(value, entry) => {
                  if (isMobile) {
                    return value.split(" ")[0];
                  }
                  return `${value} ($${(
                    entry.payload?.value || 0
                  ).toLocaleString()})`;
                }}
                wrapperStyle={{
                  fontSize: isMobile ? "9px" : "10px",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "8px",
                }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>

        {/* Mobile Legend Alternative */}
        {isMobile && chartData.length > 0 && (
          <div className="mt-2 space-y-1">
            {chartData.map((entry, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-xs"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-white/75">{entry.name}</span>
                </div>
                <span className="text-white/65 font-medium">
                  ${entry.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenueBreakdownChart;
